import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plane,
  LayoutDashboard,
  FileText,
  CheckSquare,
  Receipt,
  MapPin,
  BookOpen,
  Wallet,
  CreditCard,
  Building2,
  FileBarChart2,
  CalendarDays,
  Wrench,
  Plus,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Printer,
  DollarSign,
  Shield,
  ShieldCheck,
  AlertTriangle,
  ChevronRight,
  User,
  Users,
  BookmarkCheck,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import BookingControllerRules from '../components/travel/BookingControllerRules';
import TravelBookingDesk from './travel/TravelBookingDesk';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelDesk = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Core Data States
  const [dashboardData, setDashboardData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [reportType, setReportType] = useState('register');

  // Modals & Action States
  const [selectedReq, setSelectedReq] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStatusChoice, setBookingStatusChoice] = useState('booked'); // 'booked' | 'not_booked'
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [statementCard, setStatementCard] = useState(null);

  // Form States
  const [bookingForm, setBookingForm] = useState({
    request_id: '',
    booking_type: 'flight',
    pnr_reference: '',
    vendor_name: 'IndiGo Airlines',
    carrier_name_or_no: '6E-204',
    base_fare: 10000,
    taxes: 1800,
    convenience_fee: 200,
    credit_card_used_id: '',
    cancellation_policy: 'Non-refundable within 24 hrs',
    not_booked_reason: '',
  });

  const [actionComments, setActionComments] = useState('');
  const [assignedCardId, setAssignedCardId] = useState('');

  useEffect(() => {
    fetchDashboard();
    fetchRequests();
    fetchPolicies();
    fetchCreditCards();
    fetchVendors();
    fetchBookings();
    fetchCalendar();
  }, []);

  const fetchDashboard = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/dashboard`, { withCredentials: true });
      setDashboardData(data);
    } catch (err) {}
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true });
      setRequests(data.requests || []);
    } catch (err) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPolicies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/policies`, { withCredentials: true });
      setPolicies(data.policies || []);
    } catch (err) {}
  };

  const fetchCreditCards = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards`, { withCredentials: true });
      setCreditCards(data.credit_cards || []);
      if (data.credit_cards?.length > 0) {
        setAssignedCardId(data.credit_cards[0]._id);
      }
    } catch (err) {}
  };

  const fetchVendors = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/vendors`, { withCredentials: true });
      setVendors(data.vendors || []);
    } catch (err) {}
  };

  const fetchBookings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/bookings`, { withCredentials: true });
      setBookings(data.bookings || []);
    } catch (err) {}
  };

  const fetchCalendar = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/calendar`, { withCredentials: true });
      setCalendarEvents(data.events || []);
    } catch (err) {}
  };

  const handleFetchReport = async (type) => {
    setReportType(type);
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/reports?report_type=${type}`, { withCredentials: true });
      setReportData(data.data || []);
    } catch (err) {
      toast.error('Failed to load report');
    }
  };

  const handleProcessAction = async (reqId, action) => {
    try {
      await axios.post(
        `${API_URL}/travel-desk/requests/${reqId}/action`,
        {
          action,
          comments: actionComments,
          assigned_card_id: assignedCardId,
        },
        { withCredentials: true }
      );
      toast.success(`Request ${action}d successfully!`);
      setSelectedReq(null);
      setActionComments('');
      fetchRequests();
      fetchDashboard();
    } catch (err) {
      toast.error(`Failed to ${action} request`);
    }
  };

  const handleSaveBooking = async (e) => {
    e.preventDefault();
    try {
      if (bookingStatusChoice === 'not_booked') {
        if (!bookingForm.not_booked_reason?.trim()) {
          toast.error('Please enter the reason for not booking the ticket.');
          return;
        }
        await axios.post(`${API_URL}/travel-desk/bookings`, {
          request_id: bookingForm.request_id,
          booking_status: 'not_booked',
          booking_action: 'not_booked',
          not_booked_reason: bookingForm.not_booked_reason.trim()
        }, { withCredentials: true });
        toast.success('Recorded: Ticket Not Booked');
      } else {
        if (!bookingForm.pnr_reference?.trim()) {
          toast.error('Please enter the PNR / Reference Number.');
          return;
        }
        await axios.post(`${API_URL}/travel-desk/bookings`, {
          ...bookingForm,
          booking_status: 'confirmed'
        }, { withCredentials: true });
        toast.success('Ticket booking recorded!');
      }
      setShowBookingModal(false);
      fetchBookings();
      fetchRequests();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save booking');
    }
  };

  const handleOpenStatement = async (cardId) => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards/${cardId}/statement`, { withCredentials: true });
      setStatementCard(data.statement);
    } catch (err) {
      toast.error('Failed to load credit card statement');
    }
  };

  const subModules = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'requests', label: 'Travel Requests', icon: FileText },
    { id: 'approvals', label: 'Approval Inbox', icon: CheckSquare },
    { id: 'assigned_queue', label: 'My Assigned Applications', icon: BookmarkCheck },
    { id: 'bookings', label: 'Booking Desk', icon: Receipt },
    { id: 'booking_controllers', label: 'Booking Controller Rules', icon: ShieldCheck },
    { id: 'cabs_hotels', label: 'Cab & Hotel Bookings', icon: MapPin },
    { id: 'policies', label: 'Travel Policy Master', icon: BookOpen },
    { id: 'budgets', label: 'Travel Budgets', icon: Wallet },
    { id: 'credit_cards', label: 'Company Credit Cards', icon: CreditCard },
    { id: 'vendors', label: 'Vendor Management', icon: Building2 },
    { id: 'reports', label: 'Travel Reports', icon: FileBarChart2 },
    { id: 'settings', label: 'Settings & Calendar', icon: CalendarDays },
  ];

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Top Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-2xl flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Plane className="w-8 h-8 text-amber-400" />
            <h1 className="text-2xl font-black tracking-tight">Enterprise Travel Desk Management</h1>
          </div>
          <p className="text-xs text-blue-200">
            Corporate Travel Help Desk · Policy Enforcement · Multi-Level Approvals · Corporate Credit Cards · Booking Desk
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs font-mono font-bold bg-amber-400/20 text-amber-300 border border-amber-400/30 px-3 py-1.5 rounded-xl">
            Independent Module (Non-Payroll)
          </span>
        </div>
      </div>

      {/* Sub-Module Tabs Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-2 flex items-center gap-1.5 overflow-x-auto">
        {subModules.map((sub) => {
          const IconComp = sub.icon;
          const isActive = activeTab === sub.id;
          return (
            <button
              key={sub.id}
              onClick={() => {
                setActiveTab(sub.id);
                if (sub.id === 'reports') handleFetchReport('register');
              }}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 ${
                isActive
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <IconComp className="w-3.5 h-3.5" />
              {sub.label}
            </button>
          );
        })}
      </div>

      {/* TAB 1: DASHBOARD */}
      {activeTab === 'dashboard' && dashboardData && (
        <div className="space-y-6">
          {/* KPI Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-1">
              <div className="text-slate-400 text-xs font-bold uppercase">Total Requests</div>
              <div className="text-3xl font-black text-slate-900">{dashboardData.kpis.total_requests}</div>
              <div className="text-[11px] text-slate-500 font-medium">
                Pending: <b>{dashboardData.kpis.pending_requests}</b> | Approved: <b>{dashboardData.kpis.approved_requests}</b>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-1">
              <div className="text-slate-400 text-xs font-bold uppercase">Total Travel Spend</div>
              <div className="text-3xl font-black text-emerald-600">
                ₹ {(dashboardData.kpis.total_travel_spend || 0).toLocaleString('en-IN')}
              </div>
              <div className="text-[11px] text-slate-500 font-medium">Across all confirmed ticket bookings</div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-1">
              <div className="text-slate-400 text-xs font-bold uppercase">Credit Card Utilization</div>
              <div className="text-3xl font-black text-blue-600">{dashboardData.kpis.card_utilization_pct}%</div>
              <div className="text-[11px] text-slate-500 font-medium">
                Available Limit: ₹ {(dashboardData.kpis.available_credit_limit || 0).toLocaleString('en-IN')}
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-1">
              <div className="text-slate-400 text-xs font-bold uppercase">Trip Breakdown</div>
              <div className="text-2xl font-black text-slate-800">
                {dashboardData.kpis.domestic_trips} Domestic / {dashboardData.kpis.international_trips} Intl
              </div>
              <div className="text-[11px] text-slate-500 font-medium">Cancelled Trips: {dashboardData.kpis.cancelled_requests}</div>
            </div>
          </div>

          {/* Department Spend & Recent Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" /> Department Travel Spend Breakdown
              </h3>
              <div className="space-y-3">
                {Object.entries(dashboardData.department_spend || {}).map(([dept, amt]) => (
                  <div key={dept} className="space-y-1">
                    <div className="flex justify-between text-xs font-semibold text-slate-700">
                      <span>{dept}</span>
                      <span className="font-mono font-bold">₹ {amt.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (amt / (dashboardData.kpis.total_travel_spend || 1)) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" /> Recent Travel Requests
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {(dashboardData.recent_requests || []).map((r) => (
                  <div key={r._id} className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between text-xs">
                    <div>
                      <div className="font-bold text-slate-900">{r.employee_name} ({r.department})</div>
                      <div className="text-slate-500 text-[10px]">{r.request_number} · {r.purpose}</div>
                    </div>
                    <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: TRAVEL REQUESTS MASTER */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h3 className="font-bold text-slate-800 text-sm">All Travel Requests Master</h3>
            <span className="text-xs text-slate-500 font-bold">Total: {requests.length}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3">Req Number</th>
                  <th className="p-3">Employee</th>
                  <th className="p-3">Department</th>
                  <th className="p-3">Project</th>
                  <th className="p-3">Purpose / Category</th>
                  <th className="p-3">Route</th>
                  <th className="p-3">Approval Step</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {requests.map((r) => (
                  <tr key={r._id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono font-bold text-blue-600">{r.request_number}</td>
                    <td className="p-3 font-bold">{r.employee_name}</td>
                    <td className="p-3">{r.department_name || r.department}</td>
                    <td className="p-3 font-bold text-slate-800">{r.project_name ? `${r.project_name} (${r.project_code || 'N/A'})` : (r.project || 'N/A')}</td>
                    <td className="p-3 capitalize">{r.purpose} ({r.travel_type})</td>
                    <td className="p-3">
                      {r.journey_details?.[0]?.from_city} &rarr; {r.journey_details?.[0]?.to_city}
                    </td>
                    <td className="p-3 capitalize font-bold text-slate-600">{r.current_approval_step?.replace(/_/g, ' ')}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-800 capitalize">{r.status}</span>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => setSelectedReq(r)}
                        className="text-blue-600 font-bold hover:underline"
                      >
                        Inspect &rarr;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: APPROVAL INBOX */}
      {activeTab === 'approvals' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-4">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-blue-600" /> Actionable Approval Inbox
          </h3>

          <div className="space-y-3">
            {requests.filter(r => ['submitted', 'pending_approval'].includes(r.status)).map((r) => (
              <div key={r._id} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-mono text-xs font-bold text-blue-600">{r.request_number}</span>
                    <h4 className="font-bold text-slate-900 text-sm">{r.employee_name} — {r.purpose}</h4>
                  </div>
                  <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2.5 py-1 rounded-full">
                    Awaiting: {r.current_approval_step?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2 text-xs bg-white p-3 rounded-xl border border-slate-100">
                  <div><b>Department:</b> {r.department_name || r.department || 'N/A'}</div>
                  <div><b>Project:</b> {r.project_name ? `${r.project_name} (${r.project_code || 'N/A'})` : (r.project || 'N/A')}</div>
                  <div><b>Route:</b> {r.journey_details?.[0]?.from_city} to {r.journey_details?.[0]?.to_city}</div>
                  <div><b>Travel Class:</b> {r.booking_preferences?.flight_class || r.booking_preferences?.train_class || 'Economy'}</div>
                </div>

                {r.policy_warnings?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 p-2.5 rounded-xl text-xs text-amber-800 flex items-center gap-2 font-medium">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Policy Warnings: {r.policy_warnings.join(' | ')}</span>
                  </div>
                )}

                {r.current_approval_step === 'finance' && (
                  <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-xl space-y-2 text-xs">
                    <div className="font-bold text-indigo-900">Finance Budget &amp; Credit Card Assignment</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-600">Assign Corporate Credit Card</label>
                        <select
                          value={assignedCardId}
                          onChange={(e) => setAssignedCardId(e.target.value)}
                          className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs"
                        >
                          {creditCards.map(c => (
                            <option key={c._id} value={c._id}>{c.card_name} ({c.card_number_masked}) — Avail: ₹{c.available_limit?.toLocaleString('en-IN')}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-600">Cost Center / GL</label>
                        <input type="text" defaultValue="CC-GLOBAL-01 / GL-4890" className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                  <input
                    type="text"
                    placeholder="Approver remarks / comments..."
                    value={actionComments}
                    onChange={(e) => setActionComments(e.target.value)}
                    className="h-9 px-3 bg-white border border-slate-300 rounded-xl text-xs w-full max-w-md outline-none"
                  />

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleProcessAction(r._id, 'reject')}
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleProcessAction(r._id, 'approve')}
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-md"
                    >
                      Approve &amp; Advance Stage &rarr;
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: MY ASSIGNED APPLICATIONS */}
      {activeTab === 'assigned_queue' && (
        <TravelBookingDesk defaultTab="assigned" />
      )}

      {/* TAB 4: BOOKING DESK */}
      {activeTab === 'bookings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-500" /> Ticket &amp; Booking Desk Operations
              </h3>
              <p className="text-xs text-slate-500">Record PNR, ticket attachment, fare breakdown, and vendor billing for approved travel requests.</p>
            </div>

            <button
              onClick={() => {
                if (requests.length > 0) {
                  setBookingForm({ ...bookingForm, request_id: requests[0]._id });
                }
                setShowBookingModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Book Approved Request
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800">Confirmed Booking Register</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                  <tr>
                    <th className="p-3">Booking Ref</th>
                    <th className="p-3">PNR</th>
                    <th className="p-3">Employee</th>
                    <th className="p-3">Carrier / Flight</th>
                    <th className="p-3">Vendor</th>
                    <th className="p-3">Credit Card Used</th>
                    <th className="p-3 text-right">Total Amount</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium">
                  {bookings.map((b) => (
                    <tr key={b._id} className="hover:bg-slate-50">
                      <td className="p-3 font-mono font-bold text-blue-600">{b.booking_number}</td>
                      <td className="p-3 font-mono font-bold text-slate-900">{b.pnr_reference}</td>
                      <td className="p-3 font-bold">{b.employee_name}</td>
                      <td className="p-3">{b.carrier_name_or_no}</td>
                      <td className="p-3">{b.vendor_name}</td>
                      <td className="p-3 font-mono text-slate-600">{b.credit_card_masked}</td>
                      <td className="p-3 font-mono font-bold text-emerald-700 text-right">₹ {(b.total_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 uppercase">{b.booking_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 8: COMPANY CREDIT CARDS */}
      {activeTab === 'credit_cards' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {creditCards.map((card) => (
              <div key={card._id} className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-6 rounded-2xl shadow-xl space-y-4 border border-slate-800">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-amber-400 text-sm">{card.card_name}</span>
                  <CreditCard className="w-6 h-6 text-slate-400" />
                </div>

                <div className="font-mono text-xl tracking-widest text-slate-200">{card.card_number_masked}</div>

                <div className="grid grid-cols-2 gap-2 text-xs border-t border-slate-800 pt-3">
                  <div>
                    <div className="text-slate-400 text-[10px]">CARDHOLDER / OWNER</div>
                    <div className="font-bold">{card.cardholder_name}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-[10px]">BANK &amp; EXPIRY</div>
                    <div className="font-bold">{card.bank_name} ({card.expiry_month_year})</div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2 text-xs border-t border-slate-800">
                  <div>
                    <span className="text-slate-400">Available Limit: </span>
                    <span className="font-mono font-bold text-emerald-400">₹ {(card.available_limit || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <button
                    onClick={() => handleOpenStatement(card._id)}
                    className="px-3 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-lg text-xs"
                  >
                    View Statement
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 10: REPORTS */}
      {activeTab === 'reports' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 space-y-5">
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <FileBarChart2 className="w-4 h-4 text-blue-600" /> Travel Reports &amp; Registers
            </h3>

            <div className="flex gap-2">
              {['register', 'air', 'train', 'cab', 'hotel', 'international'].map((rep) => (
                <button
                  key={rep}
                  onClick={() => handleFetchReport(rep)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize ${
                    reportType === rep ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {rep} Report
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  {Object.keys(reportData[0] || {}).map((col) => (
                    <th key={col} className="p-3 capitalize">{col.replace(/_/g, ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium">
                {reportData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    {Object.values(row).map((val, cIdx) => (
                      <td key={cIdx} className="p-3">{String(val)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: BOOKING CONTROLLER RULES */}
      {activeTab === 'booking_controllers' && <BookingControllerRules />}

      {/* BOOKING MODAL */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-500" /> Book Approved Travel Request
              </h3>
              <button onClick={() => setShowBookingModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">&times;</button>
            </div>

            <form onSubmit={handleSaveBooking} className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Select Travel Request</label>
                <select
                  value={bookingForm.request_id}
                  onChange={(e) => setBookingForm({ ...bookingForm, request_id: e.target.value })}
                  className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-bold"
                >
                  {requests.map(r => (
                    <option key={r._id} value={r._id}>{r.request_number} — {r.employee_name} ({r.purpose})</option>
                  ))}
                </select>
              </div>

              {/* TICKET BOOKING OPTION SELECTOR */}
              <div className="space-y-1.5 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="block font-bold text-slate-800 text-[11px] uppercase tracking-wide">
                  Ticket Booking Facility Option *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBookingStatusChoice('booked')}
                    className={`py-2 px-3 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                      bookingStatusChoice === 'booked'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Ticket Booked
                  </button>

                  <button
                    type="button"
                    onClick={() => setBookingStatusChoice('not_booked')}
                    className={`py-2 px-3 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                      bookingStatusChoice === 'not_booked'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <AlertCircle className="w-3.5 h-3.5" /> Ticket Not Booked
                  </button>
                </div>
              </div>

              {/* CONDITIONAL BODY: TICKET NOT BOOKED */}
              {bookingStatusChoice === 'not_booked' ? (
                <div className="space-y-2 p-3 bg-amber-50/60 border border-amber-200 rounded-xl">
                  <div className="text-[11px] text-amber-900 font-semibold leading-relaxed">
                    Record that tickets are not booked at this time. The travel request remains approved and the employee can enter ticket details later if required.
                  </div>
                  <div>
                    <label className="block font-bold text-slate-800 mb-1">Reason for Not Booking Ticket *</label>
                    <textarea
                      rows={3}
                      value={bookingForm.not_booked_reason}
                      onChange={(e) => setBookingForm({ ...bookingForm, not_booked_reason: e.target.value })}
                      placeholder="e.g. Employee arranging own booking / Meeting tentative / Client booking / Train tickets to be purchased locally..."
                      className="w-full p-2.5 bg-white border border-slate-300 rounded-xl font-medium outline-none focus:border-amber-500"
                      required
                    />
                  </div>
                </div>
              ) : (
                /* CONDITIONAL BODY: TICKET BOOKED */
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">PNR / Reference Number *</label>
                      <input
                        type="text"
                        value={bookingForm.pnr_reference}
                        onChange={(e) => setBookingForm({ ...bookingForm, pnr_reference: e.target.value })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono uppercase font-bold"
                        placeholder="PNR12345"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Carrier / Flight / Train No *</label>
                      <input
                        type="text"
                        value={bookingForm.carrier_name_or_no}
                        onChange={(e) => setBookingForm({ ...bookingForm, carrier_name_or_no: e.target.value })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                        placeholder="6E-204"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Base Fare (₹)</label>
                      <input
                        type="number"
                        value={bookingForm.base_fare}
                        onChange={(e) => setBookingForm({ ...bookingForm, base_fare: Number(e.target.value) })}
                        className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Taxes (₹)</label>
                      <input
                        type="number"
                        value={bookingForm.taxes}
                        onChange={(e) => setBookingForm({ ...bookingForm, taxes: Number(e.target.value) })}
                        className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Fees (₹)</label>
                      <input
                        type="number"
                        value={bookingForm.convenience_fee}
                        onChange={(e) => setBookingForm({ ...bookingForm, convenience_fee: Number(e.target.value) })}
                        className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Select Corporate Credit Card</label>
                    <select
                      value={bookingForm.credit_card_used_id}
                      onChange={(e) => setBookingForm({ ...bookingForm, credit_card_used_id: e.target.value })}
                      className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                    >
                      <option value="">-- Direct Corporate Invoice --</option>
                      {creditCards.map(c => (
                        <option key={c._id} value={c._id}>{c.card_name} ({c.card_number_masked})</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button type="button" onClick={() => setShowBookingModal(false)} className="px-4 py-2 bg-slate-200 font-bold rounded-xl cursor-pointer">Cancel</button>
                <button
                  type="submit"
                  className={`px-5 py-2 font-extrabold rounded-xl shadow-md cursor-pointer transition-colors ${
                    bookingStatusChoice === 'not_booked'
                      ? 'bg-amber-600 hover:bg-amber-500 text-white'
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  }`}
                >
                  {bookingStatusChoice === 'not_booked' ? 'Record Ticket Not Booked' : 'Confirm Ticket Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREDIT CARD STATEMENT MODAL */}
      {statementCard && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Corporate Credit Card Statement ({statementCard.statement_month})</h3>
              <button onClick={() => setStatementCard(null)} className="text-slate-400 hover:text-slate-600 font-bold">&times;</button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl font-bold">
                <div>Card: {statementCard.card_name} ({statementCard.card_number_masked})</div>
                <div>Total Spent: ₹ {(statementCard.total_bookings_amount || 0).toLocaleString('en-IN')}</div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="p-2 text-left">Booking Ref</th>
                      <th className="p-2 text-left">Employee</th>
                      <th className="p-2 text-left">Vendor</th>
                      <th className="p-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {(statementCard.linked_bookings || []).map(b => (
                      <tr key={b._id}>
                        <td className="p-2 font-mono font-bold text-blue-600">{b.booking_number}</td>
                        <td className="p-2">{b.employee_name}</td>
                        <td className="p-2">{b.vendor_name}</td>
                        <td className="p-2 font-mono font-bold text-right">₹ {(b.total_amount || 0).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelDesk;
